import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from '../hooks/use-toast';
import { ShoppingBag, Shield, User, ArrowLeft } from 'lucide-react';

const UnifiedLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginType, setLoginType] = useState('admin');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await login(username, password, loginType);
    
    if (result.success) {
      toast({
        title: 'Login successful',
        description: `Welcome ${loginType === 'admin' ? 'Admin' : username}!`
      });
      navigate(loginType === 'admin' ? '/admin' : '/customer');
    } else {
      toast({
        title: 'Login failed',
        description: result.message || 'Invalid credentials',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-700">
      <div className="w-full max-w-md px-4">
        <Link to="/">
          <Button variant="ghost" className="mb-4 text-white hover:bg-white/10">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Homepage
          </Button>
        </Link>

        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-white/10 backdrop-blur-sm p-3 rounded-2xl">
              <ShoppingBag className="h-12 w-12 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">DressPay</h1>
          <p className="text-indigo-100">Your Trusted Shopping Partner</p>
        </div>

        <Card className="shadow-2xl">
          <CardHeader>
            <CardTitle className="text-2xl">Welcome Back</CardTitle>
            <CardDescription>Sign in to your account</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={loginType} onValueChange={setLoginType}>
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="admin" className="transition-smooth">
                  <Shield className="h-4 w-4 mr-2" />
                  Admin
                </TabsTrigger>
                <TabsTrigger value="customer" className="transition-smooth">
                  <User className="h-4 w-4 mr-2" />
                  Customer
                </TabsTrigger>
              </TabsList>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="username" className="text-sm font-medium">Username</Label>
                  <Input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter your username"
                    className="mt-1 transition-smooth"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="mt-1 transition-smooth"
                    required
                  />
                </div>

                <Button type="submit" className="w-full transition-smooth hover-lift" size="lg">
                  Sign In
                </Button>
              </form>
            </Tabs>

            {loginType === 'customer' && (
              <div className="mt-5 p-4 bg-green-50 rounded-lg text-sm border border-green-100">
                <p className="text-green-700 font-semibold mb-2">Customer Login:</p>
                <p className="text-green-600">Contact admin to create your account</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UnifiedLogin;
